import {useMemo, useState} from 'react';
import {Alert, Button, Form, Input, Modal} from 'antd';
import {
  CheckOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import DBModal from 'components/modal/modal';
import {
  CHANNEL_GROUPS,
  EMPTY_FORM_VALUES,
  LANGUAGE_OPTIONS,
  MAX_CONTENT_LENGTH,
  MAX_SOURCE_URL_LENGTH,
  MAX_TITLE_LENGTH,
} from './constants';
import {isHttpUrl, joinWithAnd, languageLabel, pluralise} from './utils';
import styles from './ContentTransformations.module.scss';

const matches = (label, term) =>
  label.toLowerCase().includes(term.trim().toLowerCase());

/** Brand-coloured multi-select pill. Antd's checkbox blue is not our palette. */
const TogglePill = ({label, selected, disabled, onClick}) => (
  <button
    type="button"
    disabled={disabled}
    aria-pressed={selected}
    className={`${styles.pill} ${selected ? styles.pillActive : ''}`}
    onClick={onClick}>
    <span className={styles.pillCheck}>
      {selected ? <CheckOutlined /> : null}
    </span>
    {label}
  </button>
);

const LanguagePicker = ({value = [], onChange, disabled}) => (
  <div className={styles.pillRow}>
    {LANGUAGE_OPTIONS.map(option => {
      const selected = value.includes(option.value);
      return (
        <TogglePill
          key={option.value}
          label={option.label}
          selected={selected}
          disabled={disabled}
          onClick={() =>
            onChange(
              selected
                ? value.filter(item => item !== option.value)
                : [...value, option.value],
            )
          }
        />
      );
    })}
  </div>
);

/**
 * Grouped, searchable channel picker. The grouping is presentation only — the
 * form value stays one flat array of GraphQL channel enums.
 */
const ChannelPicker = ({value = [], onChange, disabled}) => {
  const [term, setTerm] = useState('');

  const groups = useMemo(
    () =>
      CHANNEL_GROUPS.map(group => ({
        ...group,
        channels: term
          ? group.channels.filter(channel => matches(channel.label, term))
          : group.channels,
      })).filter(group => group.channels.length),
    [term],
  );

  const toggle = (channel, selected) =>
    onChange(
      selected ? value.filter(item => item !== channel) : [...value, channel],
    );

  return (
    <div className={styles.channelPicker}>
      <div className={styles.channelHead}>
        <Input
          allowClear
          disabled={disabled}
          className={styles.channelSearch}
          prefix={<SearchOutlined />}
          placeholder="Search output channels"
          value={term}
          onChange={e => setTerm(e.target.value)}
        />
        <div className={styles.channelHeadActions}>
          <span className={styles.channelCount}>
            {value.length ? `${value.length} selected` : 'None selected'}
          </span>
          {value.length && !disabled ? (
            <button
              type="button"
              className={styles.linkBtn}
              onClick={() => onChange([])}>
              Clear
            </button>
          ) : null}
        </div>
      </div>

      {groups.length ? (
        groups.map(group => (
          <div key={group.key} className={styles.channelGroup}>
            <span className={styles.channelGroupTitle}>{group.title}</span>
            <div className={styles.channelGrid}>
              {group.channels.map(channel => (
                <TogglePill
                  key={channel.value}
                  label={channel.label}
                  selected={value.includes(channel.value)}
                  disabled={disabled}
                  onClick={() =>
                    toggle(channel.value, value.includes(channel.value))
                  }
                />
              ))}
            </div>
          </div>
        ))
      ) : (
        <p className={styles.channelEmpty}>
          No output channel matches “{term}”.
        </p>
      )}
    </div>
  );
};

const GenerateContentForm = ({
  form,
  submitting,
  formError,
  onClose,
  onSubmit,
}) => {
  // Read live so the summary and the submit button describe exactly what is
  // about to be started.
  const languages = Form.useWatch('languages', form) || [];
  const channels = Form.useWatch('channels', form) || [];

  const confirmAndSubmit = values => {
    const languageNames = joinWithAnd(
      (values.languages || []).map(languageLabel),
    );
    Modal.confirm({
      title: 'Start content generation?',
      content: `This will start ${pluralise(
        values.languages?.length || 0,
        'workflow run',
      )} for ${languageNames} across ${pluralise(
        values.channels?.length || 0,
        'selected output channel',
      )}.`,
      okText: 'Start generation',
      cancelText: 'Cancel',
      onOk: () => onSubmit(values),
    });
  };

  return (
    <div className={styles.modalRoot}>
      <div className={styles.modalHeader}>
        <span className={styles.modalIcon}>
          <ThunderboltOutlined />
        </span>
        <div>
          <h2>Generate content</h2>
          <p>
            Enter the source content, select its output languages, and choose
            the transformations to generate.
          </p>
        </div>
      </div>

      {formError ? (
        <Alert
          type="error"
          showIcon
          message={formError}
          className={styles.formAlert}
        />
      ) : null}

      <Form
        form={form}
        layout="vertical"
        disabled={submitting}
        initialValues={EMPTY_FORM_VALUES}
        onFinish={confirmAndSubmit}>
        <div className={styles.modalBody}>
          <Form.Item
            name="title"
            label="Title"
            rules={[
              {
                max: MAX_TITLE_LENGTH,
                message: `Title must not exceed ${MAX_TITLE_LENGTH} characters`,
              },
            ]}>
            <Input
              placeholder="Optional title for this run"
              maxLength={MAX_TITLE_LENGTH}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="content"
            label="Source content"
            rules={[
              {
                validator: async (_, value) => {
                  if (!(value || '').trim().length) {
                    throw new Error('Content is required');
                  }
                },
              },
              {
                max: MAX_CONTENT_LENGTH,
                message: `Content must not exceed ${MAX_CONTENT_LENGTH} characters`,
              },
            ]}>
            <Input.TextArea
              rows={8}
              placeholder="Paste the source material the workflow should transform"
              maxLength={MAX_CONTENT_LENGTH}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="sourceUrl"
            label="Source URL"
            rules={[
              {
                max: MAX_SOURCE_URL_LENGTH,
                message: `Source URL must not exceed ${MAX_SOURCE_URL_LENGTH} characters`,
              },
              {
                validator: async (_, value) => {
                  if (value && value.trim() && !isHttpUrl(value.trim())) {
                    throw new Error(
                      'Source URL must be a valid HTTP or HTTPS URL',
                    );
                  }
                },
              },
            ]}>
            <Input placeholder="https://example.com/article" />
          </Form.Item>

          <Form.Item
            name="languages"
            label="Languages"
            tooltip="Each language starts its own workflow run and creates its own history record."
            rules={[
              {
                validator: async (_, value) => {
                  if (!value?.length) {
                    throw new Error(
                      'At least one content language is required',
                    );
                  }
                },
              },
            ]}>
            <LanguagePicker disabled={submitting} />
          </Form.Item>

          <Form.Item
            name="channels"
            label="Output channels"
            rules={[
              {
                validator: async (_, value) => {
                  if (!value?.length) {
                    throw new Error('At least one output channel is required');
                  }
                },
              },
            ]}>
            <ChannelPicker disabled={submitting} />
          </Form.Item>
        </div>

        <div className={styles.modalFooter}>
          <span className={styles.runHint}>
            {languages.length && channels.length
              ? `${pluralise(languages.length, 'workflow run')} — ${joinWithAnd(
                  languages.map(languageLabel),
                )} across ${pluralise(channels.length, 'output channel')}.`
              : 'Pick at least one language and one output channel.'}
          </span>
          <div className={styles.footerActions}>
            <Button onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {submitting
                ? `Starting ${pluralise(languages.length, 'workflow run')}…`
                : 'Generate content'}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export const GenerateContentModal = ({
  open,
  form,
  submitting,
  formError,
  onClose,
  onSubmit,
}) => (
  <DBModal
    isOpen={open}
    width={760}
    destroyOnClose
    // While the mutation is in flight the modal stays put: the server mints the
    // requestId, so a second submission would start duplicate runs.
    handleClose={() => {
      if (!submitting) onClose();
    }}
    content={
      <GenerateContentForm
        form={form}
        submitting={submitting}
        formError={formError}
        onClose={onClose}
        onSubmit={onSubmit}
      />
    }
  />
);
